import PageBanner from "../components/PageBanner";
import Seo from "../components/Seo";
import { Reveal, SectionLabel, OmMark } from "../components/Motion";
import { useApp } from "../AppContext";
import { C, IMAGES, GURUJI_PHOTOS } from "../content";
import { ScrollText, AlertCircle } from "lucide-react";

function Timeline({ items, t }) {
  return (
    <div className="relative pl-8 border-l-2 border-saffron-500/40 space-y-7 mt-8">
      {items.map((it, i) => (
        <Reveal key={i} delay={i * 0.06}>
          <div className="relative">
            <span className="absolute -left-[41px] top-1.5 w-4 h-4 rounded-full bg-saffron-500 border-4 border-sand-50" />
            <div className="text-saffron-600 font-semibold text-sm uppercase tracking-wider">{it.y}</div>
            <div className="text-earth-800 text-lg mt-1">{it[t]}</div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

export default function Sadgurus() {
  const { t, lang } = useApp();
  return (
    <div>
      <Seo title="The Sadgurus — Narayan Swami & Dixit Guruji" description="Life and teachings of Shri Narayananand Tirth Swami Maharaj and Brahmashri Vyankatraman Dixit Guruji, the guiding forces of the Audumbar Narayan Swami Math." />
      <PageBanner label={lang === "mr" ? "सद्‌गुरु परिचय" : "The Sadgurus"} titleObj={{ title_mr: "सद्‌गुरु परिचय", title_en: "The Sadgurus of the Math" }} image={IMAGES.courtyardShrine} />

      {/* Narayan Swami */}
      <section className="py-16 lg:py-24 max-w-[1100px] mx-auto px-4 lg:px-8" data-testid="narayan-swami">
        <Reveal>
          <div className="flex items-center gap-3 text-deepred-700 mb-3"><OmMark className="w-9 h-9" /><SectionLabel>{lang === "mr" ? "प्रथम सद्‌गुरु" : "The Sadguru"}</SectionLabel></div>
          <h2 className="font-serif-head text-4xl lg:text-6xl text-earth-900 font-bold leading-tight">{t(C.narayanSwami, "title")}</h2>
          <p className="mt-5 text-earth-800/90 text-xl leading-relaxed">{t(C.narayanSwami, "intro")}</p>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-12 mt-10 items-start">
          <Reveal>
            <p className="text-earth-800/85 text-lg leading-relaxed">{t(C.narayanSwami, "body")}</p>
            {t(C.narayanSwami, "note") && <p className="mt-4 text-sm text-earth-600 italic">{t(C.narayanSwami, "note")}</p>}
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-sand-100 border border-earth-900/10 p-6 lg:p-8">
              <div className="flex items-center gap-2 text-deepred-700 mb-2"><ScrollText size={20} /><h3 className="font-serif-head text-2xl text-earth-900">{lang === "mr" ? "जीवन कालरेखा" : "Life Timeline"}</h3></div>
              <Timeline items={C.narayanSwami.timeline} t={lang} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Dixit Guruji */}
      <section className="py-16 lg:py-24 bg-sand-100" data-testid="dixit-guruji">
        <div className="max-w-[1100px] mx-auto px-4 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3 text-deepred-700 mb-3"><OmMark className="w-9 h-9" /><SectionLabel>{lang === "mr" ? "मार्गदर्शक शक्ती" : "The Guiding Force"}</SectionLabel></div>
            <h2 className="font-serif-head text-4xl lg:text-6xl text-earth-900 font-bold leading-tight">{t(C.dixitGuruji, "title")}</h2>
            <p className="mt-5 text-earth-800/90 text-xl leading-relaxed">{t(C.dixitGuruji, "intro")}</p>
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-12 mt-10 items-start">
            <div>
              <Reveal delay={0.08}>
                <div className="rounded-3xl overflow-hidden border border-earth-900/10 mb-6 max-w-[320px] shadow-[0_20px_50px_rgba(59,34,19,0.14)]">
                  <img src={`${process.env.PUBLIC_URL}/images/guruji-portrait.webp`} alt="Brahmashri Vyankatraman Dixit Guruji" loading="lazy" className="w-full object-cover" />
                  <div className="bg-earth-900 text-sand-100 text-center py-2.5 font-serif-head text-lg">{t(C.dixitGuruji, "title")}</div>
                </div>
                <p className="text-earth-800/85 text-lg leading-relaxed">{t(C.dixitGuruji, "body")}</p>
                {t(C.dixitGuruji, "note") && (
                  <div className="mt-5 rounded-2xl border-2 border-ochre-500/40 bg-white p-5 flex gap-3" data-testid="guruji-verify-note">
                    <AlertCircle className="text-deepred-700 shrink-0 mt-1" size={20} />
                    <p className="text-earth-800/85">{t(C.dixitGuruji, "note")}</p>
                  </div>
                )}
              </Reveal>
            </div>
            <Reveal delay={0.12}>
              <div className="rounded-3xl bg-white border border-earth-900/10 p-6 lg:p-8">
                <div className="flex items-center gap-2 text-deepred-700 mb-2"><ScrollText size={20} /><h3 className="font-serif-head text-2xl text-earth-900">{lang === "mr" ? "जीवन कालरेखा" : "Life Timeline"}</h3></div>
                <Timeline items={C.dixitGuruji.timeline} t={lang} />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-12" data-testid="guruji-photos">
              <SectionLabel>{lang === "mr" ? "गुरुजींची छायाचित्रे" : "Guruji in Pictures"}</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                {GURUJI_PHOTOS.map((p, i) => (
                  <div key={i} className="rounded-2xl overflow-hidden border border-earth-900/10 aspect-[4/5] shadow-[0_16px_40px_rgba(59,34,19,0.12)]">
                    <img src={p.url} alt={p[lang] ?? p.en} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
